import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData, useNavigate } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { ReservationEventPayloadStandard } from '@r6digital/rapidstor-types/types/Events/Reservation';

import { useExternalData } from '@/hooks/useExternalData';
import Logger from '@/utils/logging';
import {
    payLaterButtonText,
    payNowButtonText,
    RapidStorSettings,
    saveQuoteButtonText,
} from '@/utils/rapidstor';
import { fadeIn } from '@/utils/ui';
import { createId } from '@paralleldrive/cuid2';

import store from '../store';
import {
    selectContactData,
    selectUpsell,
    setReservationData,
} from '../store/bookingSlice';

import Button from '../components/elements/button';
import Header from '../components/elements/header';
import Spinner from '../components/elements/spinner';
import JourneyProgress from '../components/JourneyProgress';
import OptionSelector from '../components/UnitSelect/OptionSelector';

import {
    reservationEvent,
    reservationViaMoveInEvent,
} from '../features/rapidstor/PostMessageEvents';
import { AnimationContextProvider } from '../features/rapidstor/providers/AnimationProvider';
import { DescriptorOptions } from '../features/rapidstor/UnitOptions';
import {
    ANIMATION_DELAY,
    ANIMATION_SPEED,
    cardButtonsVariants,
} from '@/features/rapidstor/animations';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import ReservationService from '@/features/rapidstor/services/ReservationService';

import classes from '@/scss/screens/customise-booking.module.scss';

import {
    usePatchReservationMutation,
    usePostTrackEventMutation,
} from '../api/rapidstor';

export default function CustomiseBooking() {
    const location = useLoaderData() as Location;
    const upSell = useSelector(selectUpsell);
    const contactData = useSelector(selectContactData);

    const navigate = useNavigate();
    const { t } = useTranslation();
    const {
        unitData,
        reservationData,
        toggleContactCard,
        selectedDescriptor,
        selectedUnitType,
        setSelectedDescriptor,
        setSelectedUnitType,
        persona,
        handleMakeReservation,
    } = useContext(JourneyContext);

    const [cookies] = useCookies(['contactData', 'sessionId']);
    const { generateExternalData } = useExternalData();
    const [patchReservation] = usePatchReservationMutation();
    const [postTrackEvent] = usePostTrackEventMutation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedID, setSelectedID] = useState<string>('');

    useEffect(() => {
        fadeIn();
    }, []);

    useEffect(() => {
        if (!unitData || !reservationData) {
            navigate('/');
        }
    }, [navigate, reservationData, unitData]);

    useEffect(() => {
        if (reservationData?.descriptor) {
            setSelectedDescriptor(reservationData.descriptor);
            setSelectedUnitType(reservationData.unitType);
            setSelectedID(reservationData.unitType._id);
        }
    }, [navigate, location, reservationData, setSelectedDescriptor, setSelectedUnitType]);

    const hasMultipleOptions = useMemo(() => {
        if (!reservationData?.descriptor) {
            return false;
        }

        const useDescriptorOptions = DescriptorOptions(
            reservationData.descriptor,
            unitData?.descriptors ?? [],
            location
        );

        if (useDescriptorOptions.isDescriptorUpsell()) {
            return (
                useDescriptorOptions.getDescriptorUpsells(unitData?.descriptors ?? [])
                    .length >= 1
            );
        } else {
            return useDescriptorOptions.getUnitTypeUpsells().length > 1;
        }
    }, [location, reservationData?.descriptor, unitData?.descriptors]);

    if (!unitData || !reservationData) {
        return null;
    }

    const constructReservationPayload = async (forMoveIn: boolean) => {
        if (!reservationData || !selectedUnitType || !selectedDescriptor) {
            return;
        }

        setIsLoading(true);
        const reservationSvc = new ReservationService(location);
        const neededDateObject = contactData?.neededDate
            ? new Date(contactData.neededDate)
            : cookies?.contactData?.neededDate
                ? new Date(cookies.contactData.neededDate)
                : new Date();
        const neededDate = neededDateObject.toISOString();

        // If there is already a reservation token stored then only patch the existing reservation with new data
        // Otherwise create a new reservation, rapidstor track event and window tracking event
        if (reservationData?.token) {
            try {
                const patchPayload = reservationSvc.buildPatchPayload(
                    reservationData.token,
                    neededDate,
                    selectedUnitType._id,
                    reservationData.deal ?? null,
                    {},
                    {}
                );
                await patchReservation(patchPayload).unwrap();

                Logger.info(
                    `[customise-booking.tsx] PATCH reservation request`,
                    location
                );
            } catch (err) {
                reservationSvc.handleError(
                    `[customise-booking.tsx] Error thrown in PATCH reservation request`,
                    JSON.stringify(err)
                );
                setIsLoading(false);
            }

            // Navigate To Booking Wizard
            navigate(`/move-in/${reservationData.token}`);
        } else {
            try {
                // Post Reservation To RapidStor Backend
                const contactDataToUse = contactData || cookies.contactData;

                const reservationDataFresh = {
                    ...reservationData,
                    descriptor: selectedDescriptor,
                    unitType: selectedUnitType,
                };

                const payload = reservationSvc.buildPostPayload(
                    reservationDataFresh,
                    false,
                    false,
                    contactDataToUse,
                    neededDate,
                    generateExternalData()
                );

                if (persona) {
                    payload.payload.persona = persona;
                }

                const result = await handleMakeReservation(payload, selectedDescriptor._id);
                if (!result) {
                    setIsLoading(false);
                    return;
                }

                Logger.info(
                    `[customise-booking.tsx] POST reservation request`,
                    location,
                    [{ name: 'rapidstor_reservationToken', value: result.token }]
                );

                // Save Reservation Data to store
                if (selectedDescriptor) {
                    Logger.info(
                        '[customise-booking.tsx] Saving fresh descriptor data to reservationData'
                    );
                    store.dispatch(
                        setReservationData({
                            ...reservationData,
                            unitType: selectedUnitType,
                            descriptor: selectedDescriptor,
                            token: result.token,
                        })
                    );
                }

                // Send Reservation Window Tracking Event
                const eventPayload = {
                    site_info: {
                        location_code: location.locationCode,
                        site_token: location.token,
                    },
                } as ReservationEventPayloadStandard;

                if (forMoveIn) {
                    reservationViaMoveInEvent(eventPayload);
                } else {
                    reservationEvent(eventPayload);
                }

                // RapidStor reservation track event
                sendReservationTrackingEvent();

                // Navigate To Move in Wizard
                navigate(`/move-in/${result.token}`);
            } catch (err) {
                reservationSvc.handleError(
                    `[customise-booking.tsx] Error thrown in POST reservation request`,
                    JSON.stringify(err),
                    [
                        {
                            name: 'rapidstor_reservationDescriptor',
                            value: reservationData.descriptor._id,
                        },
                        {
                            name: 'rapidstor_reservationUnitType',
                            value: reservationData.unitType._id,
                        },
                        {
                            name: 'rapidstor_reservationNeededDate',
                            value: neededDate,
                        },
                    ]
                );
                setIsLoading(false);
            }
        }
    };

    /**
     * NOTE: Wrapped in a try catch with non blocking error handling as this is a non-critical event
     * If this fails the user flow for reservation should not be impacted.
     */
    const sendReservationTrackingEvent = () => {
        try {
            postTrackEvent({
                locationToken: window.rapidstor_settings.rapidstor_options.locationToken,
                corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
                payload: {
                    trackingId: createId(),
                    url: window.location.href,
                    referrerUrl: document.referrer,
                    userAgent: window.navigator.userAgent,
                    action: 'reservation',
                    timestamp: new Date().toISOString(),
                },
            });
        } catch (error) {
            Logger.warn(
                `[customise-booking.tsx] Error thrown in POST track event request: ${JSON.stringify(
                    error
                )}`,
                location
            );
        }
    };

    return (
        <>
            <div style={{
                padding: '0 12px'
            }}>
                    <JourneyProgress stepsType='regular' mobile step={3} />
                    <div>
                        <div className={'d-flex'}>
                            <Header
                                level={1}
                                className={`${classes.title} ${classes.mobile}`}
                            >
                                <span className=''>
                                    {t('customise_booking:header_mobile')}
                                </span>
                            </Header>
                            <Header
                                level={1}
                                className={`${classes.title} ${classes.desktop}`}
                            >
                                <span>
                                    {t('book_unit:header_1')}
                                    <span className={classes.underline}>
                                        {t('book_unit:header_2')}
                                    </span>
                                    {t('book_unit:header_3')}
                                </span>
                            </Header>
                        </div>
                        <JourneyProgress stepsType='regular' desktop step={3} />
                    </div>
                    {reservationData && reservationData.unitType && selectedUnitType && (
                        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                            <AnimationContextProvider>
                                <div className={`${classes.mobile}`}>
                                    <OptionSelector
                                        descriptor={reservationData.descriptor}
                                        unitType={reservationData.unitType}
                                        location={location}
                                        upSell={upSell}
                                        deal={reservationData.deal ?? null}
                                        toggleContactCard={toggleContactCard}
                                        makeReservation={() =>
                                            constructReservationPayload(false)
                                        }
                                        loading={isLoading}
                                        setLoading={setIsLoading}
                                        reservationToken={reservationData?.token}
                                        selectedID={selectedID}
                                        setSelectedID={setSelectedID}
                                    />
                                </div>
                            </AnimationContextProvider>
                            <AnimationContextProvider>
                                <div className={`${classes.desktop}`}>
                                    <OptionSelector
                                        descriptor={reservationData.descriptor}
                                        unitType={reservationData.unitType}
                                        location={location}
                                        upSell={upSell}
                                        deal={reservationData.deal ?? null}
                                        toggleContactCard={toggleContactCard}
                                        makeReservation={() =>
                                            constructReservationPayload(false)
                                        }
                                        desktop={true}
                                        loading={isLoading}
                                        setLoading={setIsLoading}
                                        reservationToken={reservationData?.token}
                                        selectedID={selectedID}
                                        setSelectedID={setSelectedID}
                                    />
                                </div>
                            </AnimationContextProvider>
                        </div>
                    )}
            </div>

            {hasMultipleOptions && (
                <motion.div
                    layout={true}
                    variants={cardButtonsVariants}
                    initial='start'
                    animate='show'
                    transition={{
                        duration: ANIMATION_SPEED,
                        delay: ANIMATION_DELAY * 2,
                    }}
                >
                    <div className={`${classes.buttons} ${classes.desktop}`}>
                        {RapidStorSettings(location).showSaveQuoteButton() && (
                            <Button
                                onclick={() => {
                                    toggleContactCard('quote', selectedUnitType, reservationData.deal || undefined);
                                }}
                                disabled={isLoading}
                                type='outlineOnBackground'
                                size='medium'
                                testId='save-quote'
                            >
                                {saveQuoteButtonText(location)}
                            </Button>
                        )}
                        {RapidStorSettings(location).showReservationButton() && (
                            <Button
                                onclick={() => {
                                    toggleContactCard('reservation', selectedUnitType, reservationData.deal || undefined);
                                }}
                                disabled={isLoading}
                                type='outlineOnBackground'
                                size='medium'
                                testId='quick-reserve'
                            >
                                {payLaterButtonText(location)}
                            </Button>
                        )}
                        {RapidStorSettings(location).showMoveInButton() && (
                            <Button
                                onclick={() => {
                                    constructReservationPayload(true);
                                }}
                                disabled={isLoading}
                                type='primary'
                                size='medium'
                                testId='pay-now'
                            >
                                {isLoading ? (
                                    <Spinner inverted />
                                ) : (
                                    payNowButtonText(location)
                                )}
                            </Button>
                        )}
                    </div>
                </motion.div>
            )}
        </>
    );
}
